<template>
  <div class="deal-modal-form-wrapper">
  <v-form class="deal-modal-form">
    <v-progress-linear v-if="isLoadingOptions" indeterminate color="primary" class="mb-4" />
    <v-row>
      <template v-if="mode === 'edit'">
        <v-col cols="12" md="6">
          <v-select
            :value="selectedPipelineId"
            :items="pipelineOptions"
            item-text="label"
            item-value="id"
            :label="`${$t('DEALS.MODAL.PIPELINE')} *`"
            :disabled="isFormDisabled"
            :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
            @change="onPipelineChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            :value="formData.pipeline_stage_id"
            :items="pipelineStageOptions"
            item-text="label"
            item-value="id"
            :label="`${$t('DEALS.MODAL.STAGE')} *`"
            :disabled="isFormDisabled || !selectedPipelineId"
            :error-messages="validationErrors.pipeline_stage_id || []"
            :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
            @change="updateFormField('pipeline_stage_id', $event)"
            @input="clearValidationError('pipeline_stage_id')"
          />
        </v-col>
      </template>
      <v-col cols="12" md="6">
        <v-text-field
          :value="formData.name"
          :label="`${$t('DEALS.MODAL.DEAL_NAME')} *`"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.name || []"
          @input="onNameInput"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          :value="formData.organization_id"
          :items="organizationOptions"
          item-text="label"
          item-value="id"
          :label="`${$t('DEALS.MODAL.ORGANIZATION')} *`"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.organization_id || []"
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          @change="updateFormField('organization_id', $event)"
          @input="clearValidationError('organization_id')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <CompaniesAutocomplete
          :value="formData.supplier_ids"
          :items="companyOptions"
          multiple
          chips
          clearable
          deletable-chips
          :label="$t('DEALS.MODAL.SUPPLIERS')"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.supplier_ids || []"
          @change="onSuppliersChange"
          @company-created="onCompanyCreated"
        />
      </v-col>
      <v-col cols="12" md="6">
        <CompaniesAutocomplete
          :value="formData.client_ids"
          :items="companyOptions"
          multiple
          chips
          clearable
          deletable-chips
          :label="$t('DEALS.MODAL.CLIENTS')"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.client_ids || []"
          @change="onClientsChange"
          @company-created="onCompanyCreated"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          :value="formData.deal_type_id"
          :items="dealTypeOptions"
          item-text="label"
          item-value="id"
          :label="`${$t('DEALS.MODAL.TYPE')} *`"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.deal_type_id || []"
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          @change="onDealTypeChange"
          @input="clearValidationError('deal_type_id')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          :value="formData.incoterm_id"
          :items="incotermOptions"
          item-text="label"
          item-value="id"
          :label="`${$t('DEALS.MODAL.INCOTERM')} *`"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.incoterm_id || []"
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          @change="updateFormField('incoterm_id', $event)"
          @input="clearValidationError('incoterm_id')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          :value="formData.location"
          :label="$t('DEALS.MODAL.LOCATION')"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.location || []"
          @input="onLocationInput"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          :value="formData.deal_status_id"
          :items="dealStatusOptions"
          item-text="label"
          item-value="id"
          :label="`${$t('DEALS.MODAL.STATUS')} *`"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.deal_status_id || []"
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          @change="updateFormField('deal_status_id', $event)"
          @input="clearValidationError('deal_status_id')"
        />
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-center">
        <v-checkbox
          :input-value="formData.is_paid"
          :label="$t('DEALS.MODAL.IS_PAID')"
          :disabled="isFormDisabled"
          hide-details
          class="mt-0"
          color="primary"
          @change="updateFormField('is_paid', $event)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <div class="text-caption grey--text mb-1">{{ $t("DEALS.TRADE_SIDE.LABEL") }}</div>
        <v-btn-toggle
          :value="formData.trade_side || 'buying'"
          mandatory
          dense
          color="primary"
          class="deal-trade-side-toggle"
          :disabled="isFormDisabled"
          @change="updateFormField('trade_side', $event)"
        >
          <v-btn small value="buying">{{ $t("DEALS.TRADE_SIDE.BUYING") }}</v-btn>
          <v-btn small value="selling">{{ $t("DEALS.TRADE_SIDE.SELLING") }}</v-btn>
        </v-btn-toggle>
        <div
          v-if="validationErrors.trade_side && validationErrors.trade_side.length"
          class="v-messages error--text mt-1"
        >
          {{ validationErrors.trade_side[0] }}
        </div>
      </v-col>
      <v-col v-if="isContainerDealType" cols="12" md="6">
        <v-text-field
          :value="formData.container_number"
          :label="$t('DEALS.MODAL.CONTAINER_NUMBER')"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.container_number || []"
          @input="onContainerNumberInput"
        />
      </v-col>
      <v-col cols="12" md="6">
        <TPDatePicker
          :value="formData.container_arrival_date"
          class="mb-7 mt-0"
          :label="$t('DEALS.MODAL.CONTAINER_ARRIVAL_DATE')"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.container_arrival_date || []"
          @input="onContainerArrivalDateChange"
        />
      </v-col>
    </v-row>

    <v-subheader class="px-0 products-title">
      {{ $t("DEALS.MODAL.PRODUCTS") }}
    </v-subheader>
    <v-row
      v-for="(product, index) in formData.products || []"
      :key="product._key || product.id || index"
      align="start"
    >
      <v-col cols="12" md="5">
        <v-select
          :value="product.meat_id"
          :items="meatOptions"
          item-text="label"
          item-value="id"
          :label="`${$t('DEALS.MODAL.MEAT')} *`"
          :disabled="isFormDisabled"
          :error-messages="productErrors(index, 'meat_id')"
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          @change="onProductMeatChange(index, $event)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          :value="product.description_ids"
          :items="filteredDescriptionOptionsForProduct(product.meat_id)"
          item-text="label"
          item-value="id"
          :label="$t('DEALS.MODAL.DESCRIPTION')"
          :disabled="isFormDisabled || !product.meat_id"
          multiple
          chips
          :error-messages="productErrors(index, 'description_ids')"
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          @change="updateProduct(index, 'description_ids', $event)"
        />
      </v-col>
      <v-col cols="12" md="1" class="d-flex align-center pb-7">
        <v-btn
          icon
          color="error"
          :disabled="isFormDisabled"
          :aria-label="$t('GENERAL.LABELS.DELETE')"
          @click="removeProduct(index)"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-btn text color="primary" class="mt-0 mb-5" :disabled="isFormDisabled" @click="addProduct">
      {{ $t("DEALS.MODAL.ADD_PRODUCT") }}
    </v-btn>

    <v-subheader class="px-0 loading-dates-title">
      {{ $t("DEALS.MODAL.LOADING_DATES") }}
    </v-subheader>
    <v-row
      v-for="(loadingDate, index) in formData.loading_dates"
      :key="loadingDate._key || index"
      align="end"
    >
      <v-col cols="12" sm="8">
        <TPDatePicker
          :value="loadingDate.date"
          class="mb-7 mt-0"
          :label="`${$t('DEALS.MODAL.LOADING_DATE')} *`"
          :disabled="isFormDisabled"
          :error-messages="loadingDateErrors(index, 'date')"
          @input="updateLoadingDate(index, 'date', $event)"
        />
      </v-col>
      <v-col cols="12" sm="4" class="d-flex align-center pb-7">
        <v-btn
          icon
          color="error"
          :disabled="isFormDisabled"
          :aria-label="$t('GENERAL.LABELS.DELETE')"
          @click="removeLoadingDate(index)"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-btn text color="primary" class="mt-0 mb-5" :disabled="isFormDisabled" @click="addLoadingDate">
      {{ $t("DEALS.MODAL.ADD_LOADING_DATE") }}
    </v-btn>

    <v-row>
      <v-col cols="12">
        <v-textarea
          :value="formData.note"
          :label="$t('DEALS.MODAL.NOTE')"
          rows="3"
          :disabled="isFormDisabled"
          :error-messages="validationErrors.note || []"
          @input="onNoteInput"
        />
      </v-col>
    </v-row>
  </v-form>

  <Confirm
    v-model="completeConfirmOpen"
    :content="completeConfirmMessage"
    @confirm="confirmCompleteDeal"
    @decline="declineCompleteDeal"
  />
  </div>
</template>

<script>
import TPDatePicker from "@/views/partials/form/DatePicker.vue";
import CompaniesAutocomplete from "@/views/partials/form/CompaniesAutocomplete.vue";
import Confirm from "@/views/partials/dialogs/Confirm.vue";
import DealsService from "@/common/deals.service";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { GET_MEATS, GET_DESCRIPTIONS } from "@/store/mappingValues.module";
import { SHOW_SNACK } from "@/store/snack.module";
import { CREATE_DEAL, UPDATE_DEAL } from "@/store/deals.module";
import { GET_DEAL_STATUS_OPTIONS } from "@/store/dealStatuses.module";
import { GET_DEAL_TYPE_OPTIONS } from "@/store/dealTypes.module";
import { GET_INCOTERM_OPTIONS } from "@/store/incoterms.module";
import { GET_DEAL_DOCUMENT_TYPE_OPTIONS } from "@/store/dealDocumentTypes.module";
import { GET_PIPELINE_OPTIONS, GET_PIPELINE } from "@/store/pipelines.module";

export default {
  name: "DealModalForm",
  components: {
    TPDatePicker,
    CompaniesAutocomplete,
    Confirm,
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
    dealId: {
      type: [Number, String],
      default: null,
    },
    documents: {
      type: Array,
      default: () => [],
    },
    initialDealStatusId: {
      type: [Number, String],
      default: null,
    },
    formData: {
      type: Object,
      required: true,
    },
    selectedPipelineId: {
      type: [Number, String],
      default: null,
    },
    formPrefill: {
      type: Object,
      default: () => ({
        organization: null,
        pipeline: null,
        suppliers: [],
        clients: [],
      }),
    },
  },
  computed: {
    isFormDisabled() {
      return this.isLoadingOptions;
    },
    isContainerDealType() {
      const selectedType = this.dealTypeOptions.find(
        (item) => Number(item.id) === Number(this.formData.deal_type_id)
      );

      return selectedType?.slug === "container";
    },
  },
  data() {
    return {
      loadingDateKeySeq: 0,
      productKeySeq: 0,
      validationErrors: {},
      isLoadingOptions: false,
      organizationOptions: [],
      companyOptions: [],
      dealStatusOptions: [],
      dealTypeOptions: [],
      incotermOptions: [],
      pipelineOptions: [],
      pipelineStageOptions: [],
      meatOptions: [],
      descriptionOptions: [],
      documentTypes: [],
      completeConfirmOpen: false,
      completeConfirmMessage: "",
      pendingSubmitPayload: null,
      pendingSubmitIsUpdate: false,
    };
  },
  created() {
    this.loadOptions();
  },
  beforeDestroy() {
    this.$emit("update:is-submitting", false);
    this.$emit("update:is-loading-options", false);
  },
  watch: {
    selectedPipelineId: {
      immediate: true,
      handler(pipelineId) {
        if (this.mode === "edit" && pipelineId) {
          this.loadPipelineStages(pipelineId, this.formData.pipeline_stage_id);
        }
      },
    },
    dealTypeOptions() {
      this.setDefaultDealType();
    },
    incotermOptions() {
      this.setDefaultIncoterm();
    },
    dealStatusOptions() {
      this.setDefaultDealStatus();
    },
  },
  methods: {
    async loadOptions() {
      this.isLoadingOptions = true;
      this.$emit("update:is-loading-options", true);

      const tasks = [
        this.$store
          .dispatch(GET_ORGANIZATIONS_OPTIONS, {})
          .then((response) => {
            this.organizationOptions = Array.isArray(response?.data) ? response.data : [];
          })
          .catch(() => {
            this.organizationOptions = [];
          }),
        this.$store
          .dispatch(GET_COMPANIES_OPTIONS, { active: true })
          .then((response) => {
            this.companyOptions = (Array.isArray(response?.data) ? response.data : []).map((item) => ({
              ...item,
              id: Number(item.id),
            }));
          })
          .catch(() => {
            this.companyOptions = [];
          }),
        this.$store
          .dispatch(GET_MEATS)
          .then((response) => {
            const meats = Array.isArray(response) ? response : [];
            this.meatOptions = meats.map((item) => ({
              id: item.id,
              label: item.value || `Value #${item.id}`,
            }));
          })
          .catch(() => {
            this.meatOptions = [];
          }),
        this.$store
          .dispatch(GET_DESCRIPTIONS)
          .then((response) => {
            const descriptions = Array.isArray(response) ? response : [];
            this.descriptionOptions = descriptions.map((item) => ({
              id: item.id,
              group_value_id: item.group_value_id || null,
              label: item.value || `Value #${item.id}`,
            }));
          })
          .catch(() => {
            this.descriptionOptions = [];
          }),
        this.$store
          .dispatch(GET_DEAL_STATUS_OPTIONS)
          .then((response) => {
            this.dealStatusOptions = Array.isArray(response?.data) ? response.data : [];
          })
          .catch(() => {
            this.dealStatusOptions = [];
          }),
        this.$store
          .dispatch(GET_DEAL_TYPE_OPTIONS)
          .then((response) => {
            this.dealTypeOptions = Array.isArray(response?.data) ? response.data : [];
          })
          .catch(() => {
            this.dealTypeOptions = [];
          }),
        this.$store
          .dispatch(GET_INCOTERM_OPTIONS)
          .then((response) => {
            this.incotermOptions = Array.isArray(response?.data) ? response.data : [];
          })
          .catch(() => {
            this.incotermOptions = [];
          }),
        this.$store
          .dispatch(GET_DEAL_DOCUMENT_TYPE_OPTIONS)
          .then((response) => {
            this.documentTypes = Array.isArray(response?.data) ? response.data : [];
          })
          .catch(() => {
            this.documentTypes = [];
          }),
        this.$store
          .dispatch(GET_PIPELINE_OPTIONS)
          .then((response) => {
            this.pipelineOptions = Array.isArray(response?.data) ? response.data : [];
          })
          .catch(() => {
            this.pipelineOptions = [];
          }),
      ];

      try {
        await Promise.all(tasks);
        this.applyFormPrefill();
      } finally {
        this.isLoadingOptions = false;
        this.$emit("update:is-loading-options", false);
      }
    },
    applyFormPrefill() {
      const organization = this.formPrefill?.organization;
      if (
        organization?.id &&
        organization?.name &&
        !this.organizationOptions.some((item) => Number(item.id) === Number(organization.id))
      ) {
        this.organizationOptions = [
          ...this.organizationOptions,
          { id: organization.id, label: organization.name },
        ];
      }

      const pipeline = this.formPrefill?.pipeline;
      if (
        pipeline?.id &&
        pipeline?.name &&
        !this.pipelineOptions.some((item) => Number(item.id) === Number(pipeline.id))
      ) {
        this.pipelineOptions = [...this.pipelineOptions, { id: pipeline.id, label: pipeline.name }];
      }

      const selectedCompanies = [
        ...(this.formPrefill?.suppliers || []),
        ...(this.formPrefill?.clients || []),
      ];

      selectedCompanies.forEach((company) => {
        if (
          !company?.id ||
          !company?.name ||
          this.companyOptions.some((item) => Number(item.id) === Number(company.id))
        ) {
          return;
        }

        this.companyOptions.push({
          id: Number(company.id),
          label: company.name,
        });
      });
    },
    onSuppliersChange(value) {
      if (this.isLoadingOptions) {
        return;
      }

      this.updateFormField("supplier_ids", Array.isArray(value) ? value : []);
      this.clearValidationError("supplier_ids");
    },
    onClientsChange(value) {
      if (this.isLoadingOptions) {
        return;
      }

      this.updateFormField("client_ids", Array.isArray(value) ? value : []);
      this.clearValidationError("client_ids");
    },
    onCompanyCreated(company) {
      if (!company?.id || !company?.label) {
        return;
      }

      if (this.companyOptions.some((item) => Number(item.id) === Number(company.id))) {
        return;
      }

      this.companyOptions = [
        {
          id: Number(company.id),
          label: company.label,
        },
        ...this.companyOptions,
      ];
    },
    filteredDescriptionOptionsForProduct(meatId) {
      if (!meatId) {
        return [];
      }

      return this.descriptionOptions.filter(
        (item) => Number(item.group_value_id) === Number(meatId)
      );
    },
    loadPipelineStages(pipelineId, preserveStageId = null) {
      if (!pipelineId) {
        this.pipelineStageOptions = [];
        this.updateFormField("pipeline_stage_id", null);
        return Promise.resolve();
      }

      return this.$store
        .dispatch(GET_PIPELINE, pipelineId)
        .then((response) => {
          const stages = Array.isArray(response?.stages) ? response.stages : [];
          this.pipelineStageOptions = [...stages]
            .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
            .map((stage) => ({
              id: stage.id,
              label: stage.name,
            }));

          const preservedStageId = Number(preserveStageId);
          if (
            preservedStageId &&
            this.pipelineStageOptions.some((stage) => Number(stage.id) === preservedStageId)
          ) {
            this.updateFormField("pipeline_stage_id", preservedStageId);
            return;
          }

          this.updateFormField("pipeline_stage_id", this.pipelineStageOptions[0]?.id || null);
        })
        .catch(() => {
          this.pipelineStageOptions = [];
          this.updateFormField("pipeline_stage_id", null);
        });
    },
    updateFormField(field, value) {
      this.$emit("update:form-data", {
        ...this.formData,
        [field]: value,
      });
    },
    onNameInput(value) {
      this.updateFormField("name", value);
      this.clearValidationError("name");
    },
    onNoteInput(value) {
      this.updateFormField("note", value);
      this.clearValidationError("note");
    },
    onPipelineChange(pipelineId) {
      this.$emit("update:selected-pipeline-id", pipelineId);
      this.clearValidationError("pipeline_stage_id");
      this.loadPipelineStages(pipelineId);
    },
    onContainerArrivalDateChange(value) {
      this.updateFormField("container_arrival_date", value);
      this.clearValidationError("container_arrival_date");
    },
    onDealTypeChange(dealTypeId) {
      const selectedType = this.dealTypeOptions.find(
        (item) => Number(item.id) === Number(dealTypeId)
      );
      const nextFormData = {
        ...this.formData,
        deal_type_id: dealTypeId,
      };

      if (selectedType?.slug !== "container") {
        nextFormData.container_number = null;
      }

      this.$emit("update:form-data", nextFormData);
      this.clearValidationError("deal_type_id");
      this.clearValidationError("container_number");
    },
    onContainerNumberInput(value) {
      this.updateFormField("container_number", value);
      this.clearValidationError("container_number");
    },
    onLocationInput(value) {
      this.updateFormField("location", value);
      this.clearValidationError("location");
    },
    updateLoadingDate(index, field, value) {
      const loadingDates = this.formData.loading_dates.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        return {
          ...item,
          [field]: value,
        };
      });

      this.$emit("update:form-data", {
        ...this.formData,
        loading_dates: loadingDates,
      });
      this.clearLoadingDatesValidationErrors();
    },
    clearValidationError(field) {
      if (!this.validationErrors[field]) {
        return;
      }

      this.$delete(this.validationErrors, field);
    },
    clearLoadingDatesValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("loading_dates.")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    clearProductsValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("products.")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    updateProduct(index, field, value) {
      const products = this.formData.products.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        return {
          ...item,
          [field]: value,
        };
      });

      this.$emit("update:form-data", {
        ...this.formData,
        products,
      });
      this.clearProductsValidationErrors();
    },
    onProductMeatChange(index, meatId) {
      const product = this.formData.products[index] || {};
      let descriptionIds = Array.isArray(product.description_ids) ? [...product.description_ids] : [];

      if (descriptionIds.length) {
        const filteredOptions = this.filteredDescriptionOptionsForProduct(meatId);
        descriptionIds = descriptionIds.filter((id) =>
          filteredOptions.some((item) => Number(item.id) === Number(id))
        );
      }

      const products = this.formData.products.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        return {
          ...item,
          meat_id: meatId,
          description_ids: descriptionIds,
        };
      });

      this.$emit("update:form-data", {
        ...this.formData,
        products,
      });
      this.clearProductsValidationErrors();
    },
    addProduct() {
      this.$emit("update:form-data", {
        ...this.formData,
        products: [
          ...(this.formData.products || []),
          {
            _key: ++this.productKeySeq,
            id: null,
            meat_id: null,
            description_ids: [],
          },
        ],
      });
      this.clearProductsValidationErrors();
    },
    removeProduct(index) {
      const products = this.formData.products.filter((_, itemIndex) => itemIndex !== index);
      this.$emit("update:form-data", {
        ...this.formData,
        products,
      });
      this.clearProductsValidationErrors();
    },
    productErrors(index, field) {
      const key = `products.${index}.${field}`;
      const err = this.validationErrors[key];
      return Array.isArray(err) ? err : err ? [err] : [];
    },
    setDefaultDealType() {
      if (this.mode !== "create" || this.formData.deal_type_id || !this.dealTypeOptions.length) {
        return;
      }

      this.updateFormField("deal_type_id", this.dealTypeOptions[0].id);
    },
    setDefaultIncoterm() {
      if (this.mode !== "create" || this.formData.incoterm_id || !this.incotermOptions.length) {
        return;
      }

      const exw = this.incotermOptions.find((item) => item.code === "EXW");
      this.updateFormField(
        "incoterm_id",
        exw?.id || this.incotermOptions[0].id
      );
    },
    setDefaultDealStatus() {
      if (this.mode !== "create" || this.formData.deal_status_id || !this.dealStatusOptions.length) {
        return;
      }

      const openStatus = this.dealStatusOptions.find((item) => item.slug === "open");
      this.updateFormField(
        "deal_status_id",
        openStatus?.id || this.dealStatusOptions[0]?.id || null
      );
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    setValidationErrors(error) {
      this.validationErrors = (error && error.errors) || {};
    },
    isMovingToCompletedStatus(dealStatusId) {
      const completedStatus = this.dealStatusOptions.find((item) => item.slug === "completed");

      if (!completedStatus || Number(dealStatusId) !== Number(completedStatus.id)) {
        return false;
      }

      return Number(this.initialDealStatusId) !== Number(completedStatus.id);
    },
    getMissingRequiredDocumentNames() {
      return DealsService.getMissingRequiredDocumentNames(this.documentTypes, this.documents);
    },
    submit() {
      if (this.isLoadingOptions) {
        return;
      }

      this.validationErrors = {};
      const payload = {
        ...this.formData,
        name: (this.formData.name || "").trim(),
        location: (this.formData.location || "").trim() || null,
        note: this.formData.note || null,
        products: (this.formData.products || []).map((product) => ({
          id: product.id || undefined,
          meat_id: product.meat_id || null,
          description_ids: Array.isArray(product.description_ids) ? product.description_ids : [],
        })),
      };

      const isUpdate = this.mode === "edit" && this.dealId;

      if (this.isMovingToCompletedStatus(payload.deal_status_id)) {
        const missingDocuments = this.getMissingRequiredDocumentNames();

        if (missingDocuments.length) {
          this.pendingSubmitPayload = payload;
          this.pendingSubmitIsUpdate = isUpdate;
          this.completeConfirmMessage = this.$t("DEALS.MODAL.COMPLETE_MISSING_DOCUMENTS_CONFIRM", {
            documents: missingDocuments.join(", "),
          });
          this.completeConfirmOpen = true;
          return;
        }
      }

      this.performSubmit(payload, isUpdate);
    },
    confirmCompleteDeal() {
      this.completeConfirmOpen = false;

      if (!this.pendingSubmitPayload) {
        return;
      }

      const payload = this.pendingSubmitPayload;
      const isUpdate = this.pendingSubmitIsUpdate;
      this.pendingSubmitPayload = null;
      this.pendingSubmitIsUpdate = false;
      this.performSubmit(payload, isUpdate);
    },
    declineCompleteDeal() {
      this.completeConfirmOpen = false;
      this.pendingSubmitPayload = null;
      this.pendingSubmitIsUpdate = false;
    },
    performSubmit(payload, isUpdate) {
      this.$emit("update:is-submitting", true);

      const request = isUpdate
        ? this.$store.dispatch(UPDATE_DEAL, { id: this.dealId, params: payload })
        : this.$store.dispatch(CREATE_DEAL, payload);

      request
        .then((deal) => {
          this.$emit("update:is-submitting", false);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
          this.$emit("submitted", { deal, isUpdate });
        })
        .catch((error) => {
          this.setValidationErrors(error);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.$emit("update:is-submitting", false);
        });
    },
    loadingDateErrors(index, field) {
      const key = `loading_dates.${index}.${field}`;
      const err = this.validationErrors[key];
      return Array.isArray(err) ? err : err ? [err] : [];
    },
    addLoadingDate() {
      this.$emit("update:form-data", {
        ...this.formData,
        loading_dates: [
          ...this.formData.loading_dates,
          {
            _key: ++this.loadingDateKeySeq,
            id: null,
            date: null,
          },
        ],
      });
      this.clearLoadingDatesValidationErrors();
    },
    removeLoadingDate(index) {
      const loadingDates = this.formData.loading_dates.filter((_, itemIndex) => itemIndex !== index);
      this.$emit("update:form-data", {
        ...this.formData,
        loading_dates: loadingDates,
      });
      this.clearLoadingDatesValidationErrors();
    },
  },
};
</script>

<style scoped>
.deal-modal-form-wrapper {
  width: 100%;
}

.deal-modal-form {
  width: 100%;
}

.loading-dates-title,
.products-title {
  font-size: 1.3rem;
}
</style>
